/**
 * Configures the HubSpot chat widget.
 * Script load, dismiss UI, identify user before embed.
 *
 * @author  Vikram Singh <vikram@maropost.com>
 * @created 2026-04
 */

import type { App, InjectionKey } from "vue";
import { inject } from "vue";
import AppConfig from "../config/app-config";
import { useUserStore } from "../stores/userStore";

const SCRIPT_ID = "hs-script-loader";
const DISMISS_BTN_ID = "hs-chatbot-dismiss-button";
const STYLE_ID = "hubspot-chat-plugin-styles";
const HUBSPOT_IFRAME_CONTAINER_ID = "hubspot-messages-iframe-container";

const HUBSPOT_CHAT_WIDGET_SCALE = 0.90;
declare global {
  interface Window {
    hsConversationsSettings?: {
      loadImmediately: boolean;
      identificationEmail?: string;
      email?: string;
      name?: string;
    };
    hsConversationsOnReady?: Array<() => void>;
    HubSpotConversations?: {
      on: (event: string, callback: () => void) => void;
      widget: { remove: () => void; load: () => void };
    };
  }
}

export type HubSpotChatApi = {
  loadIfEnabled: () => void;
  enable: () => void;
  dismiss: (btn?: HTMLElement | null) => void;
};

export const hubSpotChatKey: InjectionKey<HubSpotChatApi> = Symbol("hubSpotChat");

const noopApi: HubSpotChatApi = {
  loadIfEnabled: () => {},
  enable: () => {},
  dismiss: () => {},
};

function injectDismissStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
#${HUBSPOT_IFRAME_CONTAINER_ID} {
  transform: scale(${HUBSPOT_CHAT_WIDGET_SCALE});
  transform-origin: bottom right;
}
#hs-chatbot-dismiss-button {
  position: fixed;
  bottom: 52px;
  right: 10px;
  z-index: 10000;
  cursor: pointer;
  background: rgba(110, 110, 110, 0.6);
  border-radius: 50%;
  width: 22px;
  height: 22px;
  font-size: 16px;
  display: none;
  color: #fff;
  border: 0;
}
#hs-chatbot-dismiss-button span {
  line-height: 1;
  transform: translateY(-1px);
}
#hs-chatbot-dismiss-button:hover {
  background: #1e2533;
  color: white;
}
`;
  document.head.appendChild(style);
}

function ensureDismissButton(onClick: () => void): void {
  if (document.getElementById(DISMISS_BTN_ID)) return;
  const btn = document.createElement("button");
  btn.id = DISMISS_BTN_ID;
  btn.type = "button";
  btn.setAttribute("aria-label", "Dismiss chat");
  btn.innerHTML = '<span aria-hidden="true">&times;</span>';
  btn.addEventListener("click", onClick);
  document.body.appendChild(btn);
}

function showChatChrome(): void {
  const btn = document.getElementById(DISMISS_BTN_ID);
  if (btn) btn.style.display = "block";
  /*
   * HubSpot’s iframe defaults above our dismiss control; lower it so the fixed dismiss stays clickable.
   * @param container - The container element to set the z-index of.
   * @returns void
  */
  const container = document.getElementById(HUBSPOT_IFRAME_CONTAINER_ID);
  if (container) container.style.zIndex = "1";
}

function onWidgetLoaded(): void {
  /*
   * Register after the SDK exists; `widgetLoaded` fires once the bubble/iframe is in the DOM.
  */
  window.HubSpotConversations?.on("widgetLoaded", () => showChatChrome());
}

function resolveScriptSrc(override?: string): string {
  const trimmed = override?.trim();
  if (trimmed) return trimmed;

  const host = AppConfig.HUBSPOT_CHAT_HOST;
  const portalId = AppConfig.HUBSPOT_PORTAL_ID;
  /*
   * Empty string - caller installs a no-op API so the app never touches HubSpot when env is incomplete.
  */
  if (typeof host !== "string" || !host.trim() || typeof portalId !== "string" || !portalId.trim()) {
    return "";
  }
  return `${host.replace(/\/$/, "")}/${portalId.trim()}.js`;
}

export function hubSpotChat(options?: { scriptSrc?: string }) {
  const scriptSrc = resolveScriptSrc(options?.scriptSrc);
  /*
   * Misconfigured or missing env - still register the injection key so `useHubSpotChat()` callers don’t crash.
  */
  if (!scriptSrc) {
    return {
      install(app: App) {
        app.provide(hubSpotChatKey, noopApi);
      },
    };
  }

  let scriptRequestStarted = false;

  function initScript(): void {
    const userStore = useUserStore();
    const { email, username: loginUsername } = userStore.auth.userdata;
    const username = userStore.username.trim() || loginUsername;

    const settings: NonNullable<Window["hsConversationsSettings"]> = {
      loadImmediately: true,
    };
    if (email) {
      settings.identificationEmail = email;
      settings.email = email;
    }
    if (username) settings.name = username;

    window.hsConversationsSettings = settings;
    const script = document.createElement("script");
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.id = SCRIPT_ID;
    script.onload = () => {
      /*
       * HubSpot runs this array after the SDK initializes; we only need to hook `widgetLoaded` for our chrome.
      */
      window.hsConversationsOnReady = [onWidgetLoaded];
    };
    document.head.appendChild(script);
  }

  const api: HubSpotChatApi = {
    loadIfEnabled(): void {
      /*
       * Idempotent: either we already appended the tag or a previous call is in flight.
      */
      if (scriptRequestStarted || document.getElementById(SCRIPT_ID)) return;
      scriptRequestStarted = true;
      initScript();
    },

    enable(): void {
      const scriptEl = document.getElementById(SCRIPT_ID);
      /*
       * Script present + SDK ready: user re-opened chat after dismiss — reload widget without a second network fetch.
      */
      if (scriptEl && window.HubSpotConversations) {
        window.HubSpotConversations.widget.load();
        showChatChrome();
        return;
      }
      /*
       * First open before onload: `initScript` runs; onload will queue `onWidgetLoaded` to show dismiss + z-index fix.
      */
      if (!scriptEl) {
        scriptRequestStarted = true;
        initScript();
      }
    },

    dismiss(btn?: HTMLElement | null): void {
      /*
       * Optional `btn` supports tests or programmatic callers; default is the plugin-created dismiss control.
      */
      const el = btn ?? (document.getElementById(DISMISS_BTN_ID) as HTMLElement | null);
      if (el) el.style.display = "none";
      window.HubSpotConversations?.widget.remove();
    },
  };

  return {
    install(app: App) {
      injectDismissStyles();
      ensureDismissButton(() => api.dismiss());
      app.provide(hubSpotChatKey, api);
    },
  };
}

/**
 * Use the HubSpot chat plugin.
 * 
 * @returns {HubSpotChatApi} The HubSpot chat plugin API.
 * @throws {Error} If the HubSpot chat plugin is not installed.
 */
export function useHubSpotChat(): HubSpotChatApi {
  const resolved = inject(hubSpotChatKey);
  if (!resolved) {
    throw new Error("HubSpot chat plugin is not installed (app.use(hubSpotChat()))");
  }
  return resolved;
}
